import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { motion } from 'framer-motion';

import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
// Custom components
import { HSeparator } from 'components/separator/Separator';
import DefaultAuth from 'layouts/auth/Default';
// Assets
import illustration from 'assets/img/auth/bg-auth.jpg';
import { FcGoogle } from 'react-icons/fc';
import { MdArrowBack, MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import OTPInput from 'react-otp-input';
import toast from 'react-hot-toast';
import { createAPIEndPoint } from 'config/api/api';
import { useUserProfile } from 'contexts/UserProfileContext';
import { logoutUser } from 'utils';

function SignIn() {
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorBrand = useColorModeValue('brand.700', 'brand.400');
  const textColorSecondary = 'secondaryGray.600';
  const brandStars = useColorModeValue('red.500', 'red.400');
  const navigate = useNavigate();
  const { getUser } = useUserProfile();

  const [show, setShow] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');

  const [dontShowMessage, setDontShowMessage] = useState(false);
  const handleClick = () => setShow(!show);

  const login = async (email, password) => {
    try {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_profile');

      setLoading(true);
      const data = { email, password };
      const response = await createAPIEndPoint('login').create(data);

      setTempToken(response.data.temp_token);
      setUserId(response.data.user_id); // 👈 Capture user_id here
      setLoginEmail(email);
      setShowQrCode(true);

      if (response.data.qr_code) {
        setQrCodeUrl(`data:image/png;base64,${response.data.qr_code}`);
      }
      if (!response.data.qr_code) {
        setDontShowMessage(true);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error(
        error?.response?.data?.error || 'Login failed. Please try again.',
      );
    }
  };

  const [smsMessage, setSmsMessage] = useState('');

  // --- New function to request SMS OTP ---
  // const requestSmsOtp = async () => {
  //   if (!userId) {
  //     toast.error('User ID not available.');
  //     return;
  //   }

  //   try {
  //     const data = { email: loginEmail };
  //     await createAPIEndPoint('send_sms_code').create(data);
  //     toast.success('Check your SMS for OTP.');
  //   } catch (error) {
  //     toast.error(error?.response?.data?.error || 'Failed to send SMS OTP.');
  //   }
  // };

  const requestSmsOtp = async () => {
    if (!userId) {
      setSmsMessage('User ID not available.');
      return;
    }

    try {
      const data = { email: loginEmail };
      const response = await createAPIEndPoint('send_sms_code').create(data);

      const phone = response?.data?.phone || '';
      const maskedPhone = phone
        ? `***-***-${phone.slice(-4)}`
        : 'your registered number';

      // Show message below button
      setSmsMessage(
        `Your OTP has been sent to ${maskedPhone}. Please check your messages.`,
      );
    } catch (error) {
      setSmsMessage(
        error?.response?.data?.message || 'Failed to send OTP via SMS.',
      );
    }
  };

  const [check, setCheck] = useState(false);
 
  const verifyOtp = async () => {
    if (otp.length < 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    if (otp.length === 6) {
      try {
        setCheck(true);
        const data = { token: otp, temp_token: tempToken };
        const response = await createAPIEndPoint('verify_2fa').create(data);
        localStorage.setItem('access_token', response.data.token);
        toast.success(response?.data?.message || 'OTP verified successfully!');

        const profileResp = await checkAuthProfile();
        const userRole = profileResp.data.profile.user_role;

        //         setTimeout(() => {
        //   navigate("/auth/select-app"); // ✅ New screen after OTP
        // }, 1000);

        setTimeout(() => {
          if (userRole === 'superadmin') {
            // navigate('/ai-agents', { replace: true });
            window.location.href = '/admin/ai-agents';
          } else {
            const url = response?.data?.redirect_to;
            if (url?.startsWith('http')) {
              window.location.href = url;
            } else {
              navigate(url || '/');
            }
          }
        }, 1000);

        setOtp('');
      } catch (error) {
        setCheck(false);
        toast.error(error?.response?.data?.error || 'OTP verification failed.');
      }
    }
  };

  const checkAuthProfile = async () => {
    const url = `auth_profile`;

    try {
      const response = await createAPIEndPoint(url).fetchAll();
      const profile = response.data.profile;

      const hasAuthDashboard = profile?.dashboards?.some(
        (d) => d.name?.toLowerCase() === 'auth',
      );

      if (hasAuthDashboard) {
        localStorage.setItem('user_profile', JSON.stringify(profile));
        localStorage.setItem('user_role', profile.user_role);
        console.log('Auth Profile fetched and stored:', profile);
      } else {
        localStorage.clear();
        console.warn('Auth dashboard not assigned. Cleared local storage.');
      }

      return { success: true, data: response.data };
    } catch (error) {
      setCheck(false);
      console.error('Error fetching auth profile:', error);
      return { success: false, error: error.message };
    }
  };

  // const checkDashboard = async (profileData) => {
  //   try {
  //     const url = `dashboard/check`;
  //     const response = await createAPIEndPoint(url).create({
  //       profile: profileData.profile,
  //     });
  //     setTimeout(() => navigate('/team'), 1000);
  //     console.log('Dashboard check response:', response.data);
  //     getUser();
  //     setCheck(false);
  //   } catch (error) {
  //     setCheck(false);
  //     toast.error('Dashboard verification failed. Please try again.');
  //     if (error.response.data.error.includes('Dashboard name does not match')) {
  //       toast.error('Access to this dashboard is not permitted.');
  //       setShowQrCode(false);
  //       logoutUser(navigate);
  //     }
  //   }
  // };

  useEffect(() => {
    if (otp.length === 6)
      verifyOtp(otp, tempToken, navigate).then(() => {
        checkAuthProfile();
      });
  }, [otp]);

  useEffect(() => {
    if (!showQrCode) {
      setOtp('');
    }
  }, [showQrCode]);

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email address').required('Required'),
      password: Yup.string().required('Required'),
    }),
    onSubmit: (values) => login(values.email, values.password),
  });

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        direction="column"
        align="center"
        justify="center"
        minH="100dvh"
        w="100%"
        px={{ base: 4, md: 10 }}
      >
        <Box
          w={{ base: '100%', sm: '95%', md: '480px' }}
          p={{ base: 6, sm: 8 }}
          borderRadius="xl"
          boxShadow="xl"
          bg="white"
        >
          <Box
            // mx={showQrCode ? 'auto !important' : 'unset'}
            // me={!showQrCode ? 'auto' : 'unset'}
            // display="block"
            width="100%"
          >
            <Heading
              color={'textColor'}
              fontSize={{ base: '26px', md: '28px', lg: '31px' }}
              fontWeight="700"
              textAlign={showQrCode ? 'center' : 'left'}
              mb={dontShowMessage ? '12px' : '12px'}
            >
              {/* {qrCodeUrl ? 'Verify Otp' : 'Sign In'} */}
              {/* {qrCodeUrl
              ? 'Verify Otp'
              : showQrCode
              ? 'Enter OTP'
              : 'Welcome to Dental 360'} */}
              {!showQrCode ? (
                <>Welcome to Dental 360</>
              ) : qrCodeUrl ? (
                'Verify OTP'
              ) : (
                <span style={{ marginBottom: '24px', display: 'block' }}>
                  Enter OTP
                </span>
              )}

              {/* {!showQrCode && (
              <span style={{ color: '#007aff', marginLeft: '6px' }}>
                Dental 360
              </span>
            )} */}
            </Heading>

            {!showQrCode && (
              <Text
                mb="16px"
                color={textColorSecondary}
                fontWeight="400"
                fontSize="md"
              >
                Enter your email and password to sign in!
              </Text>
            )}

            {/* {!dontShowMessage ||
            (!showQrCode && (
              <Text
                mb="16px"
                ms="4px"
                color={textColorSecondary}
                fontWeight="400"
                fontSize="md"
              >
                Enter your email and password to sign in!
              </Text>
            ))} */}
          </Box>
          <Flex
            zIndex="2"
            direction="column"
            w={{ base: '100%', md: '420px' }}
            maxW="100%"
            background="transparent"
            borderRadius="15px"
            mx={{ base: 'auto', lg: 'unset' }}
            me="auto"
            mb={{ base: '20px', md: 'auto' }}
          >
            <Flex align="center" mb="25px">
              <HSeparator />
              {/* <Text color="gray.400" mx="14px">
              or
            </Text> */}
              <HSeparator />
            </Flex>
            <form onSubmit={formik.handleSubmit}>
              {!showQrCode ? (
                <>
                  <FormControl
                    isInvalid={formik.touched.email && formik.errors.email}
                  >
                    <FormLabel
                      display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px"
                    >
                      Email<Text color={brandStars}>*</Text>
                    </FormLabel>
                    <Input
                      name="email"
                      variant="auth"
                      fontSize="sm"
                      type="email"
                      placeholder="Enter your email"
                      mb="8px"
                      fontWeight="500"
                      size="lg"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      borderColor={
                        formik.errors.email && formik.touched.email
                          ? 'red.500'
                          : 'gray.200'
                      }
                    />
                    {formik.touched.email && formik.errors.email && (
                      <Text color="red.500" fontSize="sm">
                        {formik.errors.email}
                      </Text>
                    )}
                  </FormControl>

                  <FormControl
                    isInvalid={
                      formik.touched.password && formik.errors.password
                    }
                    mt={4}
                  >
                    <FormLabel
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      display="flex"
                    >
                      Password <Text color={brandStars}>*</Text>
                    </FormLabel>
                    <InputGroup size="md">
                      <Input
                        name="password"
                        fontSize="sm"
                        placeholder="Enter your password"
                        mb="8px"
                        size="lg"
                        type={show ? 'text' : 'password'}
                        variant="auth"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        borderColor={
                          formik.errors.password && formik.touched.password
                            ? 'red.500'
                            : 'gray.200'
                        }
                      />
                      <InputRightElement
                        display="flex"
                        alignItems="center"
                        mt="4px"
                      >
                        <Icon
                          color={textColorSecondary}
                          _hover={{ cursor: 'pointer' }}
                          as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                          onClick={handleClick}
                        />
                      </InputRightElement>
                    </InputGroup>
                    {formik.touched.password && formik.errors.password && (
                      <Text color="red.500" fontSize="sm">
                        {formik.errors.password}
                      </Text>
                    )}
                  </FormControl>
                  <Flex justifyContent="flex-end" align="center" mb="24px">
                    <NavLink to="/auth/forgot-password">
                      <Text
                        // color="#007AFF"
                        color="red.500"
                        // fontSize={{ base: '14px', md: '15px' }}
                        fontWeight="500"
                        whiteSpace="nowrap"
                        cursor="pointer"
                        mb="8px"
                        transition="all 0.2s ease-in-out"
                        _hover={{
                          textDecoration: 'underline',
                        }}
                      >
                        Forgot password?
                      </Text>
                    </NavLink>
                  </Flex>
                  <Button
                    type="submit"
                    fontSize="sm"
                    variant="brand"
                    fontWeight="500"
                    w="100%"
                    h="50"
                    // mb="12px"
                  >
                    {loading ? (
                      <Flex align="center" justify="center">
                        <Spinner size="sm" mr={2} /> Signing In...
                      </Flex>
                    ) : (
                      'Sign In'
                    )}{' '}
                  </Button>
                </>
              ) : (
                <>
                  {qrCodeUrl && (
                    <>
                      <Text align="center" color={textColor} fontWeight="500">
                        Scan the QR Code to Proceed
                      </Text>
                      <img
                        src={qrCodeUrl}
                        alt="QR Code"
                        style={{
                          width: '75%',
                          height: 'auto',
                          margin: '0px auto',
                        }}
                      />
                    </>
                  )}

                  {/* OTP Input Box */}
                  <Box display="flex" justifyContent="center" my={2}>
                    {/* <OTPInput
                      value={otp}
                      onChange={(value) => setOtp(value)}
                      numInputs={6}
                      separator={<span style={{ margin: '0 8px' }}>-</span>}
                      inputStyle={{
                        width: '50px',
                        height: '50px',
                        margin: '8px 4px',
                        fontSize: '16px',
                        borderRadius: '8px',
                        border: '1px solid #ccc',
                        textAlign: 'center',
                        outline: 'none',
                      }}
                      isInputNum
                      shouldAutoFocus
                      renderInput={(props) => (
                        <input {...props} className="otp-input" />
                      )}
                    /> */}
                    <OTPInput
                      value={otp}
                      onChange={(value) => setOtp(value)}
                      numInputs={6}
                      separator={<span style={{ margin: '0 4px' }}>-</span>}
                      inputStyle={{
                        width: '45px',
                        height: '45px',
                        margin: '6px 3px',
                        fontSize: '16px',
                        borderRadius: '8px',
                        border: '1px solid #ccc',
                        textAlign: 'center',
                        outline: 'none',
                        transition: 'all 0.2s ease-in-out',
                      }}
                      isInputNum
                      shouldAutoFocus
                      renderInput={(props) => (
                        <input
                          {...props}
                          className="otp-input"
                          style={{
                            ...props.style,
                            width: 'clamp(35px, 8vw, 50px)', // Responsive width
                            height: 'clamp(35px, 8vw, 50px)', // Responsive height
                            fontSize: 'clamp(14px, 3vw, 18px)', // Adaptive font size
                            margin: 'clamp(4px, 1vw, 8px)',
                            borderRadius: '10px',
                            border: '1px solid #cbd5e0',
                            textAlign: 'center',
                          }}
                        />
                      )}
                    />
                  </Box>

                  <Button
                    onClick={verifyOtp}
                    mt={4}
                    fontSize="sm"
                    variant={'brand'}
                    fontWeight="500"
                    w="100%"
                    h="50"
                    mb="12px"
                  >
                    {check ? 'Verifying OTP...' : 'Submit OTP'}
                  </Button>

                  {/* 🔹 New SMS OTP Button */}
                  <Button
                    onClick={requestSmsOtp}
                    fontSize="sm"
                    variant="outline"
                    fontWeight="500"
                    w="100%"
                    h="50"
                    mb="12px"
                    transition="all 0.2s ease-in-out"
                    _hover={{
                      bg: 'gray.50', // background on hover
                    }}
                  >
                    Receive OTP via SMS
                  </Button>

                  {smsMessage && (
                    <Box
                      // mt="10px"
                      mb="6px"
                      textAlign="center"
                      p="10px"
                      borderRadius="md"
                      bg={
                        smsMessage.includes('Failed') ||
                        smsMessage.includes('error')
                          ? 'red.50'
                          : 'green.50'
                      }
                      border="1px solid"
                      borderColor={
                        smsMessage.includes('Failed') ||
                        smsMessage.includes('error')
                          ? 'red.200'
                          : 'green.200'
                      }
                      color={
                        smsMessage.includes('Failed') ||
                        smsMessage.includes('error')
                          ? 'red.600'
                          : 'green.700'
                      }
                      fontSize="sm"
                      fontWeight="500"
                      boxShadow="sm"
                      transition="all 0.3s ease"
                    >
                      {smsMessage}
                    </Box>
                  )}

                  {/* <Text
                  onClick={() => setShowQrCode(false)} // Return to Login
                  align="center"
                  color={textColorBrand}
                  fontWeight="500"
                  cursor="pointer"
                  fontSize={14}
                >
                  Back to Login
                </Text> */}

                  <div
                    onClick={() => {
                      setShowQrCode(false);
                      setSmsMessage(false);
                    }}
                  >
                    <motion.div
                      whileHover={{
                        scale: 1.1, // Move the arrow 5px to the right
                        transition: { duration: 0.3 },
                      }}
                      style={{ marginTop: '16px' }}
                    >
                      <Flex
                        fontSize="sm"
                        variant="outline"
                        fontWeight="500"
                        w="100%"
                        // h="50"
                        borderColor="gray.300"
                        align="center"
                        justify="center"
                        // p="10px"
                        _hover={{
                          cursor: 'pointer',
                          borderColor: 'gray.500',
                        }}
                      >
                        <MdArrowBack
                          style={{ marginRight: '8px', marginBottom: '-1px' }}
                        />
                        Back to Login
                      </Flex>
                    </motion.div>
                  </div>
                </>
              )}
            </form>
          </Flex>
        </Box>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
